import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";

// Default leaflet markers don't load with Webpack. Inline SVG pins instead.
const destinationIcon = L.divIcon({
    className: "smart-pin smart-pin-destination",
    html: `
        <div style="
            width: 28px; height: 28px;
            background: #d4af37;
            border: 3px solid #fff;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 2px 6px rgba(0,0,0,.3);
        "></div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
});

const proIcon = L.divIcon({
    className: "smart-pin smart-pin-pro",
    html: `
        <div style="
            width: 22px; height: 22px;
            background: #1a1a1a;
            border: 2px solid #d4af37;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0,0,0,.4);
        "></div>
    `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

/**
 * Map showing destination + nearest pros. Leaflet w/ free OSM tiles.
 * Lazy: parent only renders this when there's at least 1 provider with coords.
 */
export default function NearestProsMap({ destination, providers, selectedId, onSelect, height = 320 }) {
    // Force leaflet to recalculate size after mount (CSS height changes)
    useEffect(() => {
        window.dispatchEvent(new Event("resize"));
    }, []);

    const center = [destination.lat, destination.lng];
    return (
        <div
            className="rounded-sm overflow-hidden border border-white/10"
            style={{ height }}
            data-testid="nearest-pros-map"
        >
            <MapContainer
                center={center}
                zoom={11}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={center} icon={destinationIcon}>
                    <Popup>
                        <div style={{ fontFamily: "serif", fontSize: 14 }}>You · {destination.label || "destination"}</div>
                    </Popup>
                </Marker>
                <Circle
                    center={center}
                    radius={5000}
                    pathOptions={{ color: "#d4af37", weight: 1, opacity: 0.4, fillOpacity: 0.05 }}
                />
                {providers.map((p) => (
                    <Marker
                        key={p.id}
                        position={[p.base_lat, p.base_lng]}
                        icon={proIcon}
                        eventHandlers={{
                            click: () => onSelect?.(p.id === selectedId ? null : p.id),
                        }}
                    >
                        <Popup>
                            <div style={{ fontFamily: "serif", minWidth: 160 }}>
                                <strong>{p.name}</strong>
                                <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                                    {p.distance_km}km · {p.eta_minutes}min · {p.tier}
                                </div>
                                {p.rating > 0 && (
                                    <div style={{ fontSize: 11, marginTop: 2 }}>
                                        ★ {p.rating.toFixed(1)} ({p.review_count})
                                    </div>
                                )}
                                <button
                                    onClick={() => onSelect?.(p.id === selectedId ? null : p.id)}
                                    style={{
                                        marginTop: 8, padding: "4px 10px", fontSize: 10,
                                        textTransform: "uppercase", letterSpacing: 2,
                                        background: p.id === selectedId ? "#d4af37" : "#1a1a1a",
                                        color: p.id === selectedId ? "#1a1a1a" : "#d4af37",
                                        border: "1px solid #d4af37",
                                        cursor: "pointer",
                                    }}
                                    data-testid={`map-select-${p.id}`}
                                >
                                    {p.id === selectedId ? "Selected" : "Pick this pro"}
                                </button>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
}
